import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import InstallationSubpageLayout from "./installation/InstallationSubpageLayout";
import NotificationDetailSummary from "./notifications/NotificationDetailSummary";
import NotificationMaterialPicker from "./notifications/NotificationMaterialPicker";
import NotificationSortingCampaignForm from "./notifications/NotificationSortingCampaignForm";
import NotificationCampaignContext from "./notifications/NotificationCampaignContext";
import NotificationSiteMetaLine from "./notifications/NotificationSiteMetaLine";
import { formatMonitoringTakenLine } from "./collection/monitoringRowUtils";
import { buildMaterialListFromDetectedObjects } from "./notifications/notificationMaterialUtils";
import { getNonRemovalReasonList } from "./notifications/notificationNonRemovalReasons";
import { getNotificationTypeLabel } from "./notifications/notificationTypeUtils";
import {
  formattedDate,
  formattedTime,
} from "../components/formatted/FormattedDateAndTime";
import {
  buildCommentAuthorFromStorage,
  getCommentAuthorDisplayName,
  parseCommentsList,
} from "../shared/utils/commentAuthor";
import { useTranslation } from "react-i18next";
import NotificationDetailImage from "./notifications/NotificationDetailImage";
import NotificationCharacterizationForm from "./notifications/NotificationCharacterizationForm";
import NotificationCharacterizeAction from "./notifications/NotificationCharacterizeAction";
import {
  buildClientPayloadForOutput,
  resolveClientMeasurementContainerId,
  resolveWeightKgFromTonnesInput,
  shouldWriteOutputClient,
  tryParseSelectedWasteCategory,
} from "./photoCapture/photoCaptureMobileUtils";
import {
  findClientOptionValue,
  findWasteCategoryOptionValue,
  useWasteMetadataSelectOptions,
} from "./photoCapture/useWasteMetadataSelectOptions";
import {
  getAzureBlob,
  getContainerClientRegisteredInfo,
  getSuperContainerList,
  saveContainerWeight,
  createCharacReview,
} from "../services/container";
import { getCameraId } from "../shared/utils/utils";
import { LoadingScreen } from "../components/loader/LoadingScreen";
import { updateNotificationStatus } from "../services/notification";
import { useDispatch } from "react-redux";
import { fetchNotification } from "../redux/notificationSlice";
import { formattedLevelColor } from "../components/formatted/FormattedText";
import { getNotificationPeriodHours } from "./notifications/notificationPeriodUtils";
import { toast } from "react-toastify";

const AppNotificationsDetails = ({ setCookies, saveUser }) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const azureUrl = process.env.REACT_APP_AZURE_URL;
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState({});
  const [objectRemoved, setObjectRemoved] = useState(true);
  const [materialToDisplay, setMaterialToDisplay] = useState([]);
  const [reason, setReason] = useState(null);
  const [removalTime, setRemovalTime] = useState(
    formattedTime(new Date().toISOString()),
  );
  const [specificReason, setSpecificReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [weightTonnes, setWeightTonnes] = useState("");
  const [identificationReference, setIdentificationReference] = useState("");
  const [characterizationCommentText, setCharacterizationCommentText] =
    useState("");
  const [clientData, setClientData] = useState(null);
  const [wasteCategoryData, setWasteCategoryData] = useState(null);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedWasteCategory, setSelectedWasteCategory] = useState("");
  const [toCharacterization, setToCharacterization] = useState(false);
  const {
    notificationId,
    description,
    date,
    imageUrl,
    xBox,
    yBox,
    wBox,
    hBox,
    detectedObjects,
    containerId,
    type,
    wasteName,
    addressName,
    fillingLevel,
    limitFillingLevel,
  } = location.state || {};

  const isCharacterizationType =
    type === "characterization" || type === "qualicheck";

  const resolveMeasurementContainerId = useCallback(async (physicalId) => {
    if (!physicalId) {
      return physicalId;
    }
    try {
      const response = await getSuperContainerList();
      return resolveClientMeasurementContainerId(
        physicalId,
        response?.data?.superContainers,
      );
    } catch (error) {
      console.error(
        "[mobile-notification] Failed to load super containers:",
        error,
      );
      return physicalId;
    }
  }, []);

  const {
    clientOptions,
    wasteCategoryOptions,
    isLoading: isLoadingMetadataOptions,
  } = useWasteMetadataSelectOptions(isCharacterizationType);

  const reasonList = getNonRemovalReasonList(t);

  const materialList = useMemo(
    () => buildMaterialListFromDetectedObjects(detectedObjects, t),
    [detectedObjects, t],
  );

  const needsMaterialPicker =
    !isCharacterizationType &&
    type !== "filling_level" &&
    materialList.length > 1;

  const canShowImage = Boolean(imageUrl);

  useEffect(() => {
    if (materialList.length === 1) {
      setMaterialToDisplay([materialList[0]]);
      return;
    }
    setMaterialToDisplay([]);
  }, [notificationId, materialList]);

  useEffect(
    () => {
      let text = "";
      if (materialToDisplay.length > 0) {
        const materialValue = materialToDisplay[0].value; // referenceName (ex: "cardboard", "wood", etc.)
        const materialDisplayName = materialToDisplay[0].name; // Nom traduit pour l'affichage
        const number = materialToDisplay[0].count;
        if (objectRemoved) {
          text = `${parseInt(number) > 1 ? "Les " + number + " erreurs de tri" : "L'erreur de tri"} ${materialDisplayName} ${parseInt(number) > 1 ? "ont été retirées" : "a été retiré"} vers ${removalTime}`;
          if (removalTime) {
            setIsReady(true);
          } else {
            setIsReady(false);
          }
        } else {
          const reasonDetail =
            reason?.value === "other"
              ? specificReason?.trim() || reason?.label
              : reason?.label;
          text = `${parseInt(number) > 1 ? "Les " + number + " erreurs de tri" : "L'erreur de tri"} ${materialDisplayName} ${parseInt(number) > 1 ? "n'ont pas été retirées" : "n'a pas été retiré"} car ${reasonDetail?.toLowerCase()}`;
          setIsReady(Boolean(reason));
        }
        setComment({
          time: new Date().toISOString(),
          author: buildCommentAuthorFromStorage(),
          text: text,
          material: materialValue, // Stocker le referenceName au lieu du nom traduit
          number: number,
          objectRemoved: objectRemoved,
          removalTime: removalTime,
          reason: reason,
          specificReason: specificReason,
          notificationDate: date,
        });
      } else {
        setIsReady(false);
      }
    },
    // eslint-disable-next-line
    [objectRemoved, materialToDisplay, removalTime, reason, specificReason],
  );

  useEffect(() => {
    if (!clientData || !clientOptions.length) {
      return;
    }
    const match = findClientOptionValue(clientOptions, clientData);
    if (match) {
      setSelectedClient((current) => current || match);
    }
  }, [clientData, clientOptions]);

  useEffect(() => {
    if (!wasteCategoryData || !wasteCategoryOptions.length) {
      return;
    }
    const match = findWasteCategoryOptionValue(
      wasteCategoryOptions,
      wasteCategoryData,
    );
    if (match) {
      setSelectedWasteCategory((current) => current || match);
    }
  }, [wasteCategoryData, wasteCategoryOptions]);

  const handleClientChange = (value) => {
    setSelectedClient(value);
    try {
      setClientData(JSON.parse(value));
    } catch {
      // ignore
    }
  };

  const handleWasteCategoryChange = (value) => {
    setSelectedWasteCategory(value);
    try {
      setWasteCategoryData(JSON.parse(value));
    } catch {
      // ignore
    }
  };

  useEffect(
    () => {
      const fetchClientRegisteredInfo = async (id) => {
        setLoading(true);
        await updateNotificationStatus(notificationId);
        const measurementId = await resolveMeasurementContainerId(id);
        await getContainerClientRegisteredInfo(
          {
            start: new Date(new Date(date).getTime() - 1).toISOString(),
            stop: new Date(new Date(date).getTime() + 1).toISOString(),
          },
          measurementId,
        ).then((response) => {
          if (Object.keys(response?.data).length > 0) {
            setComments(parseCommentsList(response?.data?.comments));
            if (response?.data?.weight) {
              setWeightTonnes(String(response.data.weight / 1000));
            }
            setToCharacterization(Boolean(response?.data?.toCharacterization));

            // Origine des déchets (client)
            const clientRaw = response?.data?.client;
            try {
              let parsedClient = clientRaw;
              if (typeof clientRaw === "string") {
                try {
                  parsedClient = JSON.parse(clientRaw);
                } catch {
                  parsedClient = clientRaw;
                }
              }
              if (parsedClient && typeof parsedClient === "object") {
                setClientData(parsedClient);
                if (parsedClient.code) {
                  setIdentificationReference(String(parsedClient.code));
                } else if (parsedClient.clientUniqueId) {
                  setIdentificationReference(
                    String(parsedClient.clientUniqueId),
                  );
                }
              } else if (typeof parsedClient === "string") {
                setClientData({ name: parsedClient });
              }
            } catch {
              // ignore parsing errors, keep default empty label
            }

            // Dénomination déchets (waste name)
            const wasteCategoryRaw = response?.data?.waste_category;
            if (wasteCategoryRaw) {
              try {
                const parsedCategory =
                  typeof wasteCategoryRaw === "string"
                    ? JSON.parse(wasteCategoryRaw)
                    : wasteCategoryRaw;
                if (parsedCategory && parsedCategory.wasteCategoryName) {
                  setWasteCategoryData(parsedCategory);
                }
              } catch {
                // ignore parsing errors
              }
            }
          }
        });
        setLoading(false);
      };
      fetchClientRegisteredInfo(containerId);
      dispatch(fetchNotification(getNotificationPeriodHours()));
    },
    // eslint-disable-next-line
    [
      containerId,
      date,
      notificationId,
      dispatch,
      resolveMeasurementContainerId,
    ],
  );

  const saveCharacterizationData = useCallback(
    async (overrides = {}) => {
      if (!isCharacterizationType || !containerId || !imageUrl || !date) {
        return;
      }

      const nextClient = overrides.selectedClient ?? selectedClient;
      const nextWaste =
        overrides.selectedWasteCategory ?? selectedWasteCategory;
      const nextReference =
        overrides.identificationReference ?? identificationReference;
      const nextWeight = overrides.weightTonnes ?? weightTonnes;
      const nextComment = overrides.commentText ?? characterizationCommentText;

      if (
        !shouldWriteOutputClient({
          selectedClientJson: nextClient,
          selectedWasteCategoryJson: nextWaste,
          identificationReference: nextReference,
          weightInput: nextWeight,
          commentText: nextComment,
        })
      ) {
        return;
      }

      setLoading(true);

      let commentsList = parseCommentsList(comments);
      const trimmedComment = nextComment.trim();
      if (overrides.commitComment && trimmedComment) {
        commentsList = [
          ...commentsList,
          {
            time: new Date().toISOString(),
            author: buildCommentAuthorFromStorage(),
            text: trimmedComment,
          },
        ];
        setComments(commentsList);
        setCharacterizationCommentText("");
      }

      const cameraId = getCameraId(imageUrl);
      const measurementContainerId =
        await resolveMeasurementContainerId(containerId);
      const blobName = `${
        "camera-ak_" + cameraId
      }/output-client/${date.replace(/:/g, "_").slice(0, -1)}-${
        "camera-ak_" + cameraId
      }-${measurementContainerId}-client.json`;
      const blobUrl = azureUrl + blobName;

      const { clientPayload, mongoClientId } = buildClientPayloadForOutput({
        selectedClientJson: nextClient,
        identificationReference: nextReference,
      });
      const wasteCategoryPayload =
        tryParseSelectedWasteCategory(nextWaste) ?? "";
      const weightKg = resolveWeightKgFromTonnesInput(nextWeight);

      try {
        const azureResponse = await getAzureBlob({ blobUrl: blobUrl });
        let data = {
          ...azureResponse.data,
          id: measurementContainerId,
          time: date,
          modified_time: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
          blobName: blobName,
          comments: JSON.stringify(commentsList),
          client: clientPayload || azureResponse.data?.client,
          waste_category:
            wasteCategoryPayload || azureResponse.data?.waste_category,
        };
        if (weightKg !== undefined) {
          data.weight = weightKg;
        }
        if (mongoClientId) {
          data.mongoClientId = mongoClientId;
        }
        await saveContainerWeight(data);
      } catch (error) {
        if (error.message?.includes("404")) {
          const data = {
            id: measurementContainerId,
            time: date,
            modified_time: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
            blobName: blobName,
            comments: JSON.stringify(commentsList),
            client: clientPayload,
            waste_category: wasteCategoryPayload,
            ...(weightKg !== undefined ? { weight: weightKg } : {}),
            ...(mongoClientId ? { mongoClientId } : {}),
          };
          await saveContainerWeight(data);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      azureUrl,
      characterizationCommentText,
      comments,
      containerId,
      date,
      identificationReference,
      imageUrl,
      isCharacterizationType,
      resolveMeasurementContainerId,
      selectedClient,
      selectedWasteCategory,
      weightTonnes,
    ],
  );

  const handleSubmit = async () => {
    setLoading(true);
    let commentsList = parseCommentsList(comments);

    if (comment.text !== "" && Object.keys(comment).length > 0) {
      const filteredComments = commentsList.filter(
        (item) =>
          !(
            item.time.slice(0, 16) === comment.time.slice(0, 16) &&
            item.material === comment.material &&
            item.number === comment.number
          ),
      );

      commentsList = [...filteredComments, comment];

      setComment({
        time: new Date().toISOString(),
        author: buildCommentAuthorFromStorage(),
        text: "",
      });
    }

    setComments(commentsList);
    const cameraId = getCameraId(imageUrl);
    const measurementContainerId =
      await resolveMeasurementContainerId(containerId);
    const blobName = `${
      "camera-ak_" + cameraId
    }/output-client/${date.replace(/:/g, "_").slice(0, -1)}-${
      "camera-ak_" + cameraId
    }-${measurementContainerId}-client.json`;
    const blobUrl = azureUrl + blobName;

    try {
      const azureResponse = await getAzureBlob({ blobUrl: blobUrl });
      let data = azureResponse.data;
      data = {
        ...data,
        id: measurementContainerId,
        time: date,
        modified_time: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
        blobName: blobName,
        comments: JSON.stringify(commentsList),
      };
      await saveContainerWeight(data);
    } catch (error) {
      if (error.message.includes("404")) {
        const data = {
          id: measurementContainerId,
          time: date,
          modified_time: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
          blobName: blobName,
          comments: JSON.stringify(commentsList),
        };
        await saveContainerWeight(data);
      }
    }
    resetData();
    setLoading(false);
  };

  const handleSubmitSendToReview = async () => {
    if (!imageUrl || !containerId || !date) return;

    setLoading(true);
    try {
      const cameraId = getCameraId(imageUrl);
      await createCharacReview({
        container_id: containerId,
        camera_id: cameraId,
        time: date,
        imagesrc: imageUrl,
      });
      setToCharacterization(true);
      toast.success(t("Review request sent"), {
        className: "custom-toast",
      });
    } catch (error) {
      console.error("createCharacReview failed:", error);
      toast.error(t("Unable to send review request"), {
        className: "custom-toast",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetData = () => {
    setObjectRemoved(true);
    setMaterialToDisplay([]);
    setReason(null);
    setRemovalTime(formattedTime(new Date().toISOString()));
    setSpecificReason("");
    setIsReady(false);
  };

  if (!location.state) {
    return (
      <InstallationSubpageLayout
        pageTitle={t("Notification Details")}
        headerTitle={t("Notifications")}
        onBack={() => history.push("/app/notifications")}
        setCookies={setCookies}
        saveUser={saveUser}
      >
        <div className="installation-subpage-card monitoring-empty-card">
          <p className="installation-subpage-card-text">
            {t("mobile_notifications_detail_missing")}
          </p>
          <button
            type="button"
            className="installation-save-sheet-button"
            onClick={() => history.push("/app/notifications")}
          >
            {t("mobile_notifications_back_list")}
          </button>
        </div>
      </InstallationSubpageLayout>
    );
  }

  const pageTitle = getNotificationTypeLabel(type, t);

  const isSortingCampaign = type === "sorting_campaign";
  const isSortingError = type === "sorting_error";
  const isSortingAnomalyType = isSortingCampaign || isSortingError;

  const materialPickerBlock = needsMaterialPicker ? (
    <>
      <NotificationMaterialPicker
        materialList={materialList}
        materialToDisplay={materialToDisplay}
        onSelect={setMaterialToDisplay}
        embedded={isSortingAnomalyType}
      />
      {materialToDisplay.length === 0 && (
        <p className="notification-detail-material-hint">
          {t("mobile_notifications_select_material_hint")}
        </p>
      )}
    </>
  ) : null;

  const notificationDetailImage = canShowImage ? (
    <NotificationDetailImage
      imageUrl={imageUrl}
      xBox={xBox}
      yBox={yBox}
      wBox={wBox}
      hBox={hBox}
      detectedObjects={detectedObjects}
      materialToDisplay={materialToDisplay}
      compact={isSortingAnomalyType}
    />
  ) : null;

  const campaignTakenLine = date
    ? formatMonitoringTakenLine({ time: date, t })
    : "";

  const sortingCampaignForm =
    type === "sorting_campaign" && materialToDisplay.length > 0 ? (
      <NotificationSortingCampaignForm
        materialToDisplay={materialToDisplay}
        objectRemoved={objectRemoved}
        setObjectRemoved={setObjectRemoved}
        removalTime={removalTime}
        setRemovalTime={setRemovalTime}
        reason={reason}
        setReason={setReason}
        reasonList={reasonList}
        specificReason={specificReason}
        setSpecificReason={setSpecificReason}
        isReady={isReady}
        onSubmit={() => {
          if (isReady) {
            handleSubmit();
          }
        }}
      />
    ) : null;

  return (
    <InstallationSubpageLayout
      pageTitle={pageTitle}
      headerTitle={t("Notifications")}
      onBack={() => history.push("/app/notifications")}
      setCookies={setCookies}
      saveUser={saveUser}
      shellClassName={
        isSortingAnomalyType
          ? "installation-subpage-shell notification-campaign-detail-shell"
          : undefined
      }
    >
      {!isSortingAnomalyType && !isCharacterizationType && (
        <NotificationDetailSummary
          type={type}
          description={description}
          wasteName={wasteName}
          addressName={addressName}
          date={date}
        />
      )}

      {isSortingAnomalyType && imageUrl && (
        <div className="notification-campaign-layout">
          <NotificationCampaignContext
            takenLine={campaignTakenLine}
            wasteName={wasteName}
            addressName={addressName}
          />
          <div className="installation-subpage-card notification-detail-campaign-card">
            {materialPickerBlock}
            {notificationDetailImage && (
              <div className="notification-detail-image-card">
                {notificationDetailImage}
              </div>
            )}
            {sortingCampaignForm}
          </div>
        </div>
      )}

      {!isSortingAnomalyType && imageUrl && !isCharacterizationType && (
        <>
          {materialPickerBlock}
          {notificationDetailImage && (
            <div className="notification-detail-image-card">
              {notificationDetailImage}
            </div>
          )}
        </>
      )}

      <div className="notification-detail-sections">
        {isCharacterizationType && imageUrl && (
          <>
            <NotificationDetailImage
              imageUrl={imageUrl}
              xBox={xBox}
              yBox={yBox}
              wBox={wBox}
              hBox={hBox}
              detectedObjects={detectedObjects}
            />
            {type === "qualicheck" && (
              <NotificationCharacterizeAction
                toCharacterization={toCharacterization}
                disabled={loading}
                onCharacterize={handleSubmitSendToReview}
              />
            )}
            <NotificationCharacterizationForm
              selectedClient={selectedClient}
              onClientChange={handleClientChange}
              selectedWasteCategory={selectedWasteCategory}
              onWasteCategoryChange={handleWasteCategoryChange}
              identificationReference={identificationReference}
              onIdentificationReferenceChange={setIdentificationReference}
              weightTonnes={weightTonnes}
              onWeightChange={setWeightTonnes}
              commentText={characterizationCommentText}
              onCommentChange={setCharacterizationCommentText}
              clientOptions={clientOptions}
              wasteCategoryOptions={wasteCategoryOptions}
              disabled={loading || isLoadingMetadataOptions}
              onFieldCommit={saveCharacterizationData}
            />
          </>
        )}
        {type === "filling_level" && (
          <div className="installation-subpage-card notification-detail-filling-card">
            <NotificationSiteMetaLine
              wasteName={wasteName}
              addressName={addressName}
              className="notification-detail-filling-site notification-site-meta"
            />
            <div className="flex flex-row items-center justify-center gap-2 mt-2 px-8">
              <span>{fillingLevel}%</span>
              <div className="level-content w-full max-w-xs">
                <div
                  className={`level-padding ${formattedLevelColor(fillingLevel, limitFillingLevel)}`}
                  style={{ width: `${fillingLevel}%` }}
                />
              </div>
            </div>
          </div>
        )}
        {comments.length > 0 &&
          [...comments].reverse().map((item) => (
            <div key={item.time} className="notification-detail-comment">
              <div className="flex items-center justify-center text-center rounded-full bg-[#00A7A6] h-7 w-7 p-1">
                <i className="fas fa-user text-white text-xs"></i>
              </div>
              <div
                className={`flex flex-col bg-white rounded-md px-3 py-2 w-full ${comment.time === item.time ? "border-2 border-[#00A7A6]" : ""}`}
              >
                <div className="flex flex-row w-full">
                  <div className="text-xs flex gap-2 w-full">
                    <span className="text-[#00A6A7]">
                      {getCommentAuthorDisplayName(item?.author, t("Unknown"))}
                    </span>
                    <span className="text-gray-500">
                      {formattedDate(item.time, t)} - {formattedTime(item.time)}
                    </span>
                  </div>
                </div>
                <p className="text-gray-500 text-xs mt-2">{item.text}</p>
              </div>
            </div>
          ))}
      </div>
      {loading && <LoadingScreen />}
    </InstallationSubpageLayout>
  );
};

export default AppNotificationsDetails;
